import { ColumnMeta, flexRender, Table as TanTable } from '@tanstack/react-table';
import { memo } from 'react';
import Default from '~/icons/iconography/sort/default.svg';
import SortDown from '~/icons/iconography/sort/down.svg';
import SortUp from '~/icons/iconography/sort/up.svg';
import { useTranslations } from '~/services/umbraco/hooks';
import ArrowButton from '~/shared/components/ArrowButton';
import { Text } from '~/shared/components/Text';
import Select from '../Select/Select';
import { StyledEmptyBox } from '../Select/styled';
import {
    Footer,
    PageButton,
    Pagination,
    SelectorWrapper,
    StyledTable,
    TableCell,
    TableContainer,
    TableHeaderCell,
    TableHeaderText,
    TableRow,
} from './styled';

interface OrderHistoryTableProps<TData extends object> {
    table: TanTable<TData>;
    data: TData[];
    select: number[];
    align?: 'left' | 'right' | 'center';
}

interface MyColumnMeta<TData> extends ColumnMeta<TData, unknown> {
    align?: 'left' | 'right' | 'center';
    isText?: boolean;
}

const SelectMemo = memo(Select);

export function Table<TData extends object>({
    data,
    table,
    select,
}: OrderHistoryTableProps<TData>) {
    const { translate } = useTranslations();

    return (
        <>
            <TableContainer>
                {data?.length === 0 ? (
                    <StyledEmptyBox>{translate('mit-fog.noResults')}</StyledEmptyBox>
                ) : (
                    <StyledTable>
                        <thead>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <TableRow key={headerGroup.id}>
                                    {headerGroup.headers.map((header, index) => (
                                        <TableHeaderCell
                                            key={`${header.id}-${index}`}
                                            id={header.id}
                                            align={
                                                (
                                                    header.column.columnDef
                                                        .meta as MyColumnMeta<TData>
                                                )?.align
                                            }
                                            style={{
                                                width: header.getSize(),
                                            }}
                                        >
                                            {(header.column.columnDef.meta as MyColumnMeta<TData>)
                                                ?.isText === true ? (
                                                <Text variant="display6">
                                                    {flexRender(
                                                        header.column.columnDef.header,
                                                        header.getContext(),
                                                    )}
                                                </Text>
                                            ) : (
                                                <button
                                                    onClick={header.column.getToggleSortingHandler()}
                                                >
                                                    <TableHeaderText variant="display6">
                                                        {flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext(),
                                                        )}
                                                    </TableHeaderText>
                                                    {{
                                                        asc: <SortUp />,
                                                        desc: <SortDown />,
                                                    }[header.column.getIsSorted() as string] ?? (
                                                        <Default />
                                                    )}
                                                </button>
                                            )}
                                        </TableHeaderCell>
                                    ))}
                                </TableRow>
                            ))}
                        </thead>
                        <tbody>
                            {table.getRowModel().rows.map((row) => {
                                return (
                                    <TableRow key={row.id}>
                                        {row.getVisibleCells().map((cell, index) => (
                                            <TableCell
                                                key={`${cell.id}-${index}`}
                                                style={{
                                                    textAlign: (cell.column.columnDef.meta as any)
                                                        ?.align,
                                                }}
                                            >
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext(),
                                                )}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                );
                            })}
                        </tbody>
                    </StyledTable>
                )}
            </TableContainer>
            {data?.length ? (
                <Footer>
                    <Pagination>
                        <PageButton onClick={() => table.setPageIndex(0)}>
                            {translate('mit-fog.pagination.firstPage')}
                        </PageButton>
                        <ArrowButton
                            disabled={!table.getCanPreviousPage()}
                            onClick={table.previousPage}
                            orientation="left"
                        />
                        <Text>
                            {translate('mit-fog.pagination.page')}{' '}
                            {table.getState().pagination.pageIndex + 1} af {table.getPageCount()}
                        </Text>
                        <ArrowButton
                            disabled={!table.getCanNextPage()}
                            onClick={table.nextPage}
                            orientation="right"
                        />
                        <PageButton onClick={() => table.setPageIndex(table.getPageCount() - 1)}>
                            {translate('mit-fog.pagination.lastPage')}
                        </PageButton>
                    </Pagination>
                    <SelectorWrapper>
                        <SelectMemo
                            data={select}
                            setPageSize={table.setPageSize}
                            value={table.getState().pagination.pageSize}
                        />
                    </SelectorWrapper>
                </Footer>
            ) : null}
        </>
    );
}
